<template>
  <div
    :class="isDialog ? 'detail-card d-flex flex-column' : ''"
    :style="isDialog ? 'height:100vh;' : ''"
  >
    <!-- Toolbar: solo en modo diálogo -->
    <div v-if="isDialog" class="dlg-toolbar flex-shrink-0 d-flex align-center px-3">
      <v-btn icon dark @click.stop="close">
        <v-icon>fas fa-times</v-icon>
      </v-btn>
      <v-avatar color="primary" size="36" class="ml-2 mr-3">
        <v-icon small color="white">fas fa-file-contract</v-icon>
      </v-avatar>
      <div v-if="work_order">
        <div class="subtitle-1 font-weight-black white--text lh-1">
          Post Costeo #{{ work_order.id }}
          <span class="caption grey--text ml-2">CN #{{ work_order.sales_note_data.id }}</span>
        </div>
        <div class="caption grey--text lh-1 mt-1">{{ work_order.customer_name }}</div>
      </div>
      <v-spacer />
    </div>

    <!-- Contenido principal (scrollable en modo diálogo) -->
    <div :class="isDialog ? 'flex-grow-1 overflow-y-auto' : ''">
      <v-container class="modern-detail-view pb-12">
        <v-skeleton-loader v-if="loading" type="article, card, table, table" class="pa-6" />

        <div v-else-if="work_order">
          <!-- Header con botón volver: solo en modo ruta -->
          <v-row v-if="!isDialog" class="mb-8 align-center px-4 pt-4">
            <v-btn icon @click.stop="close" class="mr-4 grey lighten-4 shadow-sm">
              <v-icon color="primary" small>fas fa-chevron-left</v-icon>
            </v-btn>
            <div class="d-flex align-center">
              <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
                <v-icon color="white">fas fa-file-contract</v-icon>
              </v-avatar>
              <div>
                <div class="text-h4 font-weight-black primary--text lh-1">
                  Post Costeo <span class="secondary--text ml-1">#{{ work_order.id }}</span>
                  <span class="grey--text text--lighten-1 ml-4 font-weight-light" style="font-size: 0.7em;">CN #{{ work_order.sales_note_data.id }}</span>
                </div>
                <div class="mt-1 d-flex align-center">
                  <span class="caption grey--text font-weight-bold uppercase tracking-widest">
                    {{ work_order.customer_name }}
                  </span>
                  <span class="mx-2 grey--text opacity-40">•</span>
                  <span class="caption grey--text font-weight-black uppercase">
                    Finalizada: {{ work_order.completed_at | moment('DD/MM/YYYY HH:mm') }}
                  </span>
                </div>
              </div>
            </div>
            <v-spacer></v-spacer>
            <div class="d-flex flex-column align-end">
              <div class="caption grey--text font-weight-black uppercase mb-1">Creada por</div>
              <div class="body-2 font-weight-bold d-flex align-center">
                <v-avatar size="20" color="grey lighten-4" class="mr-2">
                  <v-icon x-small color="grey darken-1">fas fa-user</v-icon>
                </v-avatar>
                {{ work_order.created_by }}
              </div>
            </div>
          </v-row>

          <!-- Meta Information Grid -->
          <v-row class="mb-8 px-4">
            <v-col cols="12" md="3">
              <v-card outlined class="modern-card pa-4 shadow-xs h-100 border-left-primary">
                <div class="d-flex justify-space-between align-start mb-2">
                  <div class="caption grey--text font-weight-black uppercase">PROYECTO / REFERENCIA</div>
                  <div class="text-h5 font-weight-black primary--text mt-n1">CN #{{ work_order.sales_note_data.id }}</div>
                </div>
                <div class="body-1 font-weight-black secondary--text lh-1-2 mb-3">{{ work_order.sales_note_data.name }}</div>
                <div class="d-flex align-center">
                  <v-avatar size="18" color="grey lighten-4" class="mr-2">
                    <v-icon x-small color="grey darken-1">fas fa-user-tie</v-icon>
                  </v-avatar>
                  <span class="caption grey--text font-weight-bold">{{ work_order.sales_note_data.seller }}</span>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined class="modern-card pa-4 shadow-xs h-100">
                <div class="caption grey--text font-weight-black uppercase mb-2">CLIENTE</div>
                <div class="body-2 font-weight-bold grey--text text--darken-4 mb-1">{{ work_order.sales_note_data.customer }}</div>
                <div class="caption grey--text">RUT Proyectado de cotización</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined class="modern-card pa-4 shadow-xs h-100">
                <div class="caption grey--text font-weight-black uppercase mb-2">FECHA COMPROMISO</div>
                <div class="body-1 font-weight-black primary--text mb-1">
                  {{ work_order.delivery_date | moment('DD/MM/YYYY') }}
                </div>
                <div class="caption grey--text font-weight-bold">{{ work_order.delivery_date | moment('HH:mm') }} hrs</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined class="modern-card pa-4 shadow-xs h-100">
                <div class="caption grey--text font-weight-black uppercase mb-2">ESTADO FINALIZACIÓN</div>
                <div class="d-flex align-center mb-1">
                  <v-icon color="success" x-small class="mr-2">fas fa-check-circle</v-icon>
                  <span class="body-2 font-weight-black success--text uppercase">Procesado completo</span>
                </div>
                <div class="caption grey--text font-weight-bold">
                  {{ work_order.completed_at | moment('DD/MM/YYYY HH:mm') }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Resume Component (RunningCost) -->
          <v-card outlined class="modern-card shadow-sm mb-8 overflow-hidden">
            <div class="bg-lightgrey px-6 py-3 border-bottom d-flex align-center">
              <v-icon x-small color="primary" class="mr-2">fas fa-chart-pie</v-icon>
              <span class="caption font-weight-black uppercase tracking-widest grey--text">Resumen Ejecutivo de Costos</span>
            </div>
            <div class="pa-6">
              <running-cost :work_order="work_order" />
            </div>
          </v-card>

          <!-- Main Products Detail Table -->
          <v-card outlined class="modern-card shadow-sm overflow-hidden mb-8">
            <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
              <v-icon x-small color="primary" class="mr-2">fas fa-boxes</v-icon>
              <span class="caption font-weight-black uppercase tracking-widest grey--text">Desglose por Producto ({{ (work_order.products || []).length }})</span>
              <v-spacer></v-spacer>
              <span class="caption grey--text font-italic">Haga clic para ver el detalle técnico y consumos reales</span>
            </div>

            <v-data-table
              :headers="productsTableHeaders"
              :items="work_order.products"
              item-key="id"
              show-expand
              single-expand
              :items-per-page="-1"
              hide-default-footer
              class="modern-table"
            >
              <template v-slot:item.sku="{ item }">
                <span class="caption font-weight-black grey--text">{{ item.sku }}</span>
              </template>

              <template v-slot:item.name="{ item }">
                <span class="body-2 font-weight-bold primary--text">{{ item.name }}</span>
              </template>

              <template v-slot:item.qty="{ item }">
                <span class="body-2 font-weight-black">{{ item.qty }}</span>
              </template>

              <template v-slot:item.totalMts2="{ item }">
                <v-chip x-small class="font-weight-black px-2">{{ item.mts2 | number('0.00') }} m²</v-chip>
              </template>

              <template v-slot:item.total_sales_price="{ item }">
                <span class="body-2 font-weight-black grey--text text--darken-3">{{ (item.sales_price || 0) | moneda }}</span>
              </template>

              <template v-slot:item.total_estimated_cost="{ item }">
                <span class="body-2 font-weight-bold orange--text text--darken-2">{{ (item.sales_cost || 0) | moneda }}</span>
              </template>

              <template v-slot:item.real_price="{ item }">
                <span class="body-2 font-weight-black" :class="(item.total_cost || 0) > (item.sales_cost || 0) ? 'error--text' : 'success--text'">
                  {{ (item.total_cost || 0) | moneda }}
                </span>
              </template>

              <!-- Expanded Detail Section -->
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0 bg-lightgrey border-left-primary">
                  <div class="pa-8">
                    <!-- Inner Product Info -->
                    <div v-if="item.notes" class="mb-6 pa-4 rounded-lg bg-orange-light border-warning-light">
                      <div class="caption font-weight-black orange--text text--darken-4 uppercase mb-1">NOTAS DEL PRODUCTO</div>
                      <div class="body-2 italic">{{ item.notes }}</div>
                    </div>

                    <!-- Three Columns: Processes, Materials Quoted, Items Consumed -->
                    <v-row>
                      <!-- 1. Processes -->
                      <v-col cols="12">
                        <div class="d-flex align-center mb-4">
                          <v-avatar size="24" color="primary" class="mr-2"><v-icon x-small color="white">fas fa-cog</v-icon></v-avatar>
                          <span class="text-subtitle-2 font-weight-black secondary--text uppercase">Procesos de Producción / Bitácora Real</span>
                        </div>
                        <v-card outlined class="modern-card overflow-hidden shadow-xs">
                          <v-simple-table dense class="modern-simple-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-left py-4">Proceso</th>
                                  <th class="text-center py-4">Unidad</th>
                                  <th class="text-right py-4">Total Teórico</th>
                                  <th class="text-right py-4">Costo Teórico</th>
                                  <th class="text-right py-4">Cant. Real</th>
                                  <th class="text-right py-4">Costo Real</th>
                                  <th class="text-center py-4">Bitácora</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(process, index) in item.processes" :key="process.id">
                                  <td class="py-2">
                                    <div class="d-flex align-center">
                                      <span class="caption grey--text mr-2">{{ index + 1 }}</span>
                                      <span class="body-2 font-weight-bold grey--text text--darken-3">{{ process.name }}</span>
                                      <v-icon v-if="process.reprocess" x-small color="error" class="ml-2" title="Reproceso">fas fa-clock-rotate-left</v-icon>
                                    </div>
                                  </td>
                                  <td class="text-center caption">{{ process.unit_name }}</td>
                                  <td class="text-right body-2 font-weight-medium">
                                    {{ process.unit == 2 ? process.qtypp * item.qty + (process.merma || 0) : (process.unit == 1 ? process.qtypp * item.mts2 : process.qtypp) | number('0,0.00') }}
                                  </td>
                                  <td class="text-right body-2 orange--text text--darken-2 font-weight-bold">
                                    {{ (process.unit == 1 ? (process.min_price > (process.price * item.mts2) ? process.min_price : (process.price * process.qtypp * item.mts2)) : (process.unit == 2 ? (process.min_price > (process.price * item.qty) ? process.min_price : (process.price * (process.qtypp * item.qty + process.merma))) : process.price)) | moneda }}
                                  </td>
                                  <td class="text-right body-2 font-weight-black primary--text">{{ process.completed_mts2_qty.qty | number('0,0[.]00') }}</td>
                                  <td class="text-right body-2 font-weight-black success--text">{{ process.cost | moneda }}</td>
                                  <td class="text-center">
                                    <v-menu offset-y max-width="500">
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon small v-bind="attrs" v-on="on" class="grey lighten-4"><v-icon x-small>fas fa-eye</v-icon></v-btn>
                                      </template>
                                      <v-card class="modern-card shadow-lg pa-4">
                                        <div class="caption font-weight-black mb-3 border-bottom pb-2">REGISTROS DE TRABAJO</div>
                                        <v-simple-table v-if="process.bitacora && process.bitacora.length > 0" dense>
                                          <thead><tr><th>Fecha</th><th>Usuario</th><th class="text-right">Mts²</th><th class="text-right">Cant.</th></tr></thead>
                                          <tbody>
                                            <tr v-for="reg in process.bitacora" :key="reg.id">
                                              <td class="caption">{{ reg.created_at | moment('DD/MM/YYYY') }}</td>
                                              <td class="caption font-weight-bold">{{ reg.created_by.first_name }}</td>
                                              <td class="text-right caption">{{ reg.mts2 }}</td>
                                              <td class="text-right caption font-weight-black">{{ reg.qty }}</td>
                                            </tr>
                                          </tbody>
                                        </v-simple-table>
                                        <div v-else class="pa-4 text-center caption grey--text italic">Sin registros en bitácora</div>
                                      </v-card>
                                    </v-menu>
                                  </td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card>
                      </v-col>

                      <!-- 2. Quoted Materials -->
                      <v-col cols="12" md="6">
                        <div class="d-flex align-center mb-4 mt-4">
                          <v-avatar size="24" color="orange darken-1" class="mr-2"><v-icon x-small color="white">fas fa-clipboard-list</v-icon></v-avatar>
                          <span class="text-subtitle-2 font-weight-black secondary--text uppercase">Materiales Cotizados (Teórico)</span>
                        </div>
                        <v-card outlined class="modern-card overflow-hidden shadow-xs">
                          <v-simple-table dense class="modern-simple-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-left py-4">Material</th>
                                  <th class="text-center py-4">Unid.</th>
                                  <th class="text-right py-4">Total</th>
                                  <th class="text-right py-4">Costo Teórico</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(supply, sIdx) in item.supplies" :key="sIdx">
                                  <td class="py-2 d-flex align-center">
                                    <v-icon v-if="supply.additional" x-small color="orange" class="mr-2">fas fa-plus</v-icon>
                                    <span class="body-2 grey--text text--darken-3">{{ supply.name }}</span>
                                  </td>
                                  <td class="text-center caption">{{ supply.unit_name }}</td>
                                  <td class="text-right body-2">{{ (supply.unit == 1 ? supply.qty * item.mts2 : (supply.unit == 2 ? supply.qty * item.qty : supply.qty)) | number('0,0.00') }}</td>
                                  <td class="text-right body-2 font-weight-bold orange--text text--darken-3">{{ supply.estimated_cost.total | moneda }}</td>
                                </tr>
                                <tr v-if="!item.supplies || item.supplies.length === 0">
                                  <td colspan="4" class="text-center pa-4 caption grey--text italic">No se cotizaron materiales específicos</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card>
                      </v-col>

                      <!-- 3. Items Consumed (Real Warehouse) -->
                      <v-col cols="12" md="6">
                        <div class="d-flex align-center mb-4 mt-4">
                          <v-avatar size="24" color="success" class="mr-2"><v-icon x-small color="white">fas fa-warehouse</v-icon></v-avatar>
                          <span class="text-subtitle-2 font-weight-black secondary--text uppercase">Artículos Consumidos (Real bodega)</span>
                        </div>
                        <v-card outlined class="modern-card overflow-hidden shadow-xs">
                          <v-simple-table dense class="modern-simple-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-left py-4">Artículo</th>
                                  <th class="text-right py-4">P. Promedio</th>
                                  <th class="text-right py-4">Cant.</th>
                                  <th class="text-right py-4">Subtotal Real</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(consume, cIdx) in item.consumption" :key="cIdx">
                                  <td class="py-2">
                                    <div class="caption grey--text font-weight-black mb-n1">{{ consume.article.sku }}</div>
                                    <div class="body-2 font-weight-bold secondary--text lh-1-2">{{ consume.article.name }}</div>
                                  </td>
                                  <td class="text-right caption">{{ consume.articlePrice | moneda }}</td>
                                  <td class="text-right body-2 font-weight-black">{{ consume.qty }} {{ consume.unit }}</td>
                                  <td class="text-right body-2 font-weight-black success--text">{{ consume.total | moneda }}</td>
                                </tr>
                                <tr v-if="!item.consumption || item.consumption.length === 0">
                                  <td colspan="4" class="text-center pa-4 caption grey--text italic">Sin consumos de bodega registrados</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </td>
              </template>
            </v-data-table>
          </v-card>

          <!-- Bottom Grids: Additional Costs, Buying Orders, Warehouse consumption -->
          <v-row>
            <!-- Additional Costs OT -->
            <v-col cols="12" md="6" v-if="work_order.additional_costs && work_order.additional_costs.length > 0">
              <v-card outlined class="modern-card shadow-sm overflow-hidden h-100">
                <div class="px-6 py-3 border-bottom bg-lightgrey d-flex align-center">
                  <v-icon x-small color="orange" class="mr-2">fas fa-plus-circle</v-icon>
                  <span class="caption font-weight-black uppercase tracking-widest grey--text">Gastos Adicionales de la OT</span>
                </div>
                <v-data-table
                  :headers="additionalCostsHeaders"
                  :items="work_order.additional_costs"
                  hide-default-footer
                  dense
                  class="modern-table"
                >
                  <template v-slot:item.price="{ item }">
                    <span class="body-2 font-weight-black orange--text text--darken-3">{{ item.price | moneda }}</span>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>

            <!-- Additional Costs CN (Proyect) -->
            <v-col cols="12" md="6" v-if="work_order.sales_note_additional_costs && work_order.sales_note_additional_costs.length > 0">
              <v-card outlined class="modern-card shadow-sm overflow-hidden h-100">
                <div class="px-6 py-3 border-bottom bg-lightgrey d-flex align-center">
                  <v-icon x-small color="secondary" class="mr-2">fas fa-file-invoice-dollar</v-icon>
                  <span class="caption font-weight-black uppercase tracking-widest grey--text">Costos Adicionales Cotización</span>
                </div>
                <v-data-table
                  :headers="proyectCostsHeaders"
                  :items="work_order.sales_note_additional_costs"
                  hide-default-footer
                  dense
                  class="modern-table"
                >
                  <template v-slot:item.totalCost="{ item }">
                    <span class="body-2 font-weight-bold grey--text text--darken-3">{{ item.totalCost | moneda }}</span>
                  </template>
                  <template v-slot:item.totalPrice="{ item }">
                    <v-chip x-small :color="item.totalPrice > item.totalCost ? 'success' : 'error'" dark label class="font-weight-black">
                      {{ item.totalPrice | moneda }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>

            <!-- Purchase Orders Associated -->
            <v-col cols="12" v-if="work_order.buying_orders && work_order.buying_orders.length > 0">
              <v-card outlined class="modern-card shadow-sm overflow-hidden mb-8">
                <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
                  <v-icon x-small color="primary" class="mr-2">fas fa-shopping-cart</v-icon>
                  <span class="caption font-weight-black uppercase tracking-widest grey--text">Órdenes de Compra Asociadas</span>
                </div>
                <v-data-table
                  :headers="purchaseOrdersHeaders"
                  :items="work_order.buying_orders"
                  show-expand
                  single-expand
                  class="modern-table"
                  hide-default-footer
                >
                  <template v-slot:item.id="{ item }">
                    <span class="body-2 font-weight-black primary--text">#{{ item.id }}</span>
                  </template>
                  <template v-slot:item.date="{ item }">
                    <span class="caption grey--text font-weight-bold">{{ item.created_at | moment("DD/MM/YYYY") }}</span>
                  </template>
                  <template v-slot:item.provider_name="{ item }">
                    <span class="body-2 font-weight-bold secondary--text">{{ item.provider_name }}</span>
                  </template>
                  <template v-slot:item.total="{ item }">
                    <span class="body-2 font-weight-black">{{ item.total | moneda }}</span>
                  </template>
                  <template v-slot:item.estatus="{ item }">
                    <v-chip x-small label outlined :color="getStatusColor(item)" class="font-weight-black px-2">{{ getStatusName(item) }}</v-chip>
                  </template>
                  <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="pa-0 bg-lightgrey">
                      <div class="pa-6">
                        <detailed-purchase-order :purchaseOrder="item" :inSet="true" />
                      </div>
                    </td>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>

            <!-- Warehouse Log -->
            <v-col cols="12">
              <v-card outlined class="modern-card shadow-sm overflow-hidden">
                <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
                  <v-icon x-small color="success" class="mr-2">fas fa-stream</v-icon>
                  <span class="caption font-weight-black uppercase tracking-widest grey--text">Bitácora General de Consumos de Bodega</span>
                </div>
                <div class="pa-0">
                  <consume-o-t-bitacora :stand_alone="true" :workorder_id="work_order.id" />
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </div>
  </div>
</template>

<script>
import { getPostCostWorkOrders } from '@/api/reports'
import DetailedPurchaseOrder from '@/components/stocks/DetailedPurchaseOrder'
import RunningCost from '@/components/production/workorder/RunningCost.vue'
import ConsumeOTBitacora from '@/views/Reports/ConsumeOTBitacora'
import notifications from '@/utils/notifications'

export default {
  name: 'DetailedPostCost',
  components: {
    DetailedPurchaseOrder,
    RunningCost,
    ConsumeOTBitacora
  },

  props: {
    work_order_id: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      loading: false,
      work_order: null,
      expanded: [],
      productsTableHeaders: [
        { text: 'Código', value: 'sku', class: 'table-header', width: '120px' },
        { text: 'Descripción del Producto', value: 'name', class: 'table-header' },
        { text: 'Cant.', value: 'qty', class: 'table-header', align: 'end', width: '80px' },
        { text: 'Total Mts²', value: 'totalMts2', class: 'table-header', align: 'end', width: '110px' },
        { text: 'Venta Neto', value: 'total_sales_price', class: 'table-header', align: 'end', width: '130px' },
        { text: 'Costo Teor.', value: 'total_estimated_cost', class: 'table-header', align: 'end', width: '130px' },
        { text: 'Costo Real', value: 'real_price', class: 'table-header', align: 'end', width: '130px' },
        { text: '', value: 'data-table-expand', class: 'table-header', width: '50px' },
      ],
      additionalCostsHeaders: [
        { text: 'Descripción', value: 'name', class: 'table-header' },
        { text: 'Monto Neto', value: 'price', class: 'table-header', align: 'end', width: '150px' },
      ],
      proyectCostsHeaders: [
        { text: 'Concepto Cotizado', value: 'name', class: 'table-header' },
        { text: 'Cant.', value: 'qty', class: 'table-header', align: 'center', width: '70px' },
        { text: 'Costo Tot.', value: 'totalCost', class: 'table-header', align: 'end', width: '130px' },
        { text: 'Venta Tot.', value: 'totalPrice', class: 'table-header', align: 'end', width: '130px' },
      ],
      purchaseOrdersHeaders: [
        { text: 'Orden', value: 'id', class: 'table-header', width: '100px' },
        { text: 'Fecha', value: 'date', class: 'table-header', width: '120px' },
        { text: 'Proveedor', value: 'provider_name', class: 'table-header' },
        { text: 'Total Neto', value: 'total', class: 'table-header', align: 'end', width: '130px' },
        { text: 'Estado', value: 'estatus', class: 'table-header', align: 'center', width: '120px' },
        { text: '', value: 'data-table-expand', class: 'table-header', width: '50px' },
      ],
      estatuses: [
        { value: 1, text: 'Ingresada', color: 'info' },
        { value: 2, text: 'Aprobada', color: 'success' },
        { value: 4, text: 'Recepcionada', color: 'primary' },
        { value: 3, text: 'Anulada', color: 'error' },
      ],
    }
  },

  computed: {
    otId() {
      return this.work_order_id || this.$route.params.id
    },
    isDialog() {
      return this.work_order_id != null
    }
  },

  watch: {
    work_order_id(newId) {
      if (newId) this.fetch(newId)
    }
  },

  methods: {
    close() {
      if (this.isDialog) this.$emit('close')
      else this.$router.push('/reports/post-cost')
    },

    async fetch(id) {
      this.loading = true
      try {
        const data = await getPostCostWorkOrders(id)

        data.products = data.products || []
        data.additional_costs = data.additional_costs || []
        data.buying_orders = data.buying_orders || []
        data.sales_note_data = data.sales_note_data || { id: '', name: '', customer: '', seller: '' }
        data.sales_note_additional_costs = data.sales_note_data.additional_costs || []

        this.work_order = data
        this.work_order.products = this.work_order.products.map(product => {
          product.processes = product.processes || []
          product.consumption = product.consumption || []
          product.supplies = product.supplies || []

          product.production_cost = product.processes.reduce((acc, val) => acc + parseFloat(val.cost || 0), 0)
          product.consumption_cost = product.consumption.reduce((acc, val) => acc + parseFloat(val.total || 0), 0)
          product.total_cost = product.production_cost + product.consumption_cost

          return product
        })
      } catch (err) {
        notifications.showError('Error cargando OT: ' + err)
        this.close()
      } finally {
        this.loading = false
      }
    },

    getStatusName(item) {
      return this.estatuses.find(i => i.value == item.estatus)?.text || 'S/N'
    },

    getStatusColor(item) {
      return this.estatuses.find(i => i.value == item.estatus)?.color || 'grey'
    },
  },

  created() {
    if (this.otId) {
      this.fetch(this.otId)
    }
  }
}
</script>

<style scoped>
.detail-card { background-color: #f4f7f9; border-radius: 0; }
.dlg-toolbar { height: 64px; min-height: 64px; max-height: 64px; background-color: #1A1F2E; }

.modern-detail-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.bg-orange-light { background-color: #fffaf4; }
.border-warning-light { border: 1px solid #ffe8cc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25 !important; }
.uppercase { text-transform: uppercase; letter-spacing: 0.8px; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1px; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 60px !important; border-bottom: 1px solid #f0f3f5 !important; }

.modern-simple-table >>> thead th { background-color: #f1f5f8 !important; text-transform: uppercase; font-size: 0.65rem !important; color: #546e7a !important; font-weight: 900 !important; }
.modern-simple-table >>> td { border-bottom: 1px solid #f0f3f5 !important; }

.opacity-40 { opacity: 0.4; }
.italic { font-style: italic; }
</style>
